import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { FormSchema, FormControlSchema } from '../../../models/form-schema.model';
import { EnvironmentService } from '../../../services/common/environment.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { getFieldMaxLength } from '../../../utils/helpers';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { KeyFilterModule } from 'primeng/keyfilter';
import { WarningMessageComponent, WarningMessage } from '../../common/warning-message/warning-message.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { evaluateConditional } from '../../../services/mbrs/conditional/conditional-field.evaluator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  identificationTypeFieldFor,
  isNumericOnlyIdentificationField,
} from '../../../utils/identification-field.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
@Component({
  selector: 'app-role6-120200-director-business-review',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    ButtonModule,
    InputNumber,
    TooltipModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    KeyFilterModule,
    WarningMessageComponent,
    NegativeHighlightDirective,
    SchemaNumericDirective,
    PlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role6-120200-director-business-review.component.html',
  styleUrls: ['./role6-120200-director-business-review.component.css']
})
export class Role6120200DirectorBusinessReviewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  businessReviewForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Director Business Review (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.directorBusinessReview
    );
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '6',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔵 [Role6] ngOnInit started');

    try {
      await this.loadSchema();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Schema loaded');

      await this.loadTypeDict();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Type dict loaded');

      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Form built, pristine:', this.businessReviewForm.pristine, 'dirty:', this.businessReviewForm.dirty);
      
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Saved state loaded, pristine:', this.businessReviewForm.pristine, 'dirty:', this.businessReviewForm.dirty);
      
      this.setupStatePersistence();
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] State persistence setup');
      
      this.setupStateListener();
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] State listener setup');
      
      this.applyConditionalRequired();

      this.businessReviewForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.applyConditionalRequired());

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Conditional validation setup');
      
      this.setupWarningEvaluation(); // Setup warning system
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Warning evaluation setup');
      

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ [Role6] Form title updated');

      // Deterministic replacement for the legacy in-loadSavedState
      // setTimeout(100 → 600) finalization. Extracted to finalizeInit() so the
      // non-init caller (onCancelConfirmed) also re-finalizes after its own
      // loadSavedState() call — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🟢 [Role6] ngOnInit completed - Form pristine:', this.businessReviewForm.pristine, 'dirty:', this.businessReviewForm.dirty);
    } catch (error) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Report through the trace instead of a fixed sentence. The toast now
 // names this sheet, the steps it reached, and whether the schema was
 // missing, unreadable, or fine but the form could not be built —
 // `classifySheetInitError` infers the last of those by elimination.
 // Must run BEFORE the loading flag is cleared below: `fail()` closes the
 // trace, which is what stops the finalize mark on the next line from
 // recording this failed init as a success.
 this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.directorBusinessReview),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((directorBusinessReviewData) => {
        if (directorBusinessReviewData && Object.keys(directorBusinessReviewData).length > 0) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('📥 Backend data received for directorBusinessReview:', directorBusinessReviewData);

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Patch the form with the new data (no date conversion needed for this form)
          this.businessReviewForm.patchValue(directorBusinessReviewData, { emitEvent: false });

          this.applyConditionalRequired();

          // Mark form as pristine after loading to prevent showing 'Edited' status
          this.businessReviewForm.markAsPristine();

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('✓ Form patched with backend data');

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-120200.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Schema loaded:', this.schema);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach(key => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map((value: string) => ({
        label: value,
        value: value
      }));
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Building form with schema:', this.schema);
    
    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip section headers (they're just for display)
      if (control.type === 'SectionHeader' || control.ui.control === 'section-header') {
        return;
      }

      const validators = [];

      // Add required validator (if unconditionally required).
      //
      // A field is considered unconditionally required when EITHER:
      //   - The schema's top-level `control.required === true` flag is set, OR
      //   - The schema declares `validators: [{ name: 'required' }]` with NO
      //     conditionalOn property (i.e. an unconditional required entry in
      //     the validators array).
      //
      // Previously only the first source was honoured, which silently
      // dropped enforcement for fields declared via the second form. The
      // pure role6DirectorBusinessReviewValidator mirrors this expanded
      // logic so the parity-check harness stays green.
      const hasUnconditionalRequiredViaValidators =
        control.validators?.some(
          (validator) => validator.name === 'required' && (validator as any).conditionalOn === undefined
        ) ?? false;
      const isUnconditionallyRequired =
        (control.required ?? false) || hasUnconditionalRequiredViaValidators;
      if (isUnconditionallyRequired && !this.hasConditionalRequirement(control)) {
        validators.push(Validators.required);
      }

      // Numeric/format constraints (min/max/pattern) from the schema, derived
      // per control via the shared util — see schema-validators.util.
      validators.push(...mapSchemaValidators(control));

      // Get default value
      let defaultValue: any = '';
      if (control.type === 'NonNegativeInteger') {
        defaultValue = null;
      } else if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.businessReviewForm = new FormGroup(formControls);
    
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form built with controls:', Object.keys(formControls));
  }

  /**
   * Check if a control has conditional requirement
   */
  private hasConditionalRequirement(control: FormControlSchema): boolean {
    return control.validators?.some(
      validator => validator.name === 'required' && (validator as any).conditionalOn !== undefined
    ) || false;
  }

  /**
   * Single source for conditional required-state: for every control with a
   * `validation`/`both` conditional, arm `Validators.required` when the rule is
   * active, else clear. Reads the same evaluator as shouldShowField/the pure
   * validator/the projector.
   *
   * Safe to blanket setValidators/clearValidators here because buildForm() emits
   * no *reactive* maxLength validator for role 6's one conditional field (#f7);
   * its 255-char cap is the template `[maxlength]` attribute only. If buildForm
   * ever starts honouring schema `maxLength` as a Validators.maxLength, this
   * method must preserve it instead of overwriting the validator array.
   */
  private applyConditionalRequired(): void {
    if (!this.schema?.controls) return;
    const data = this.businessReviewForm.getRawValue();
    const context = { filingInfo: this.stateService.getActiveFilingInfoData() };
    for (const control of this.schema.controls) {
      const rule = control.conditional;
      if (!rule || rule.appliesTo === 'visibility') continue;
      const formControl = this.businessReviewForm.get(control.qname);
      if (!formControl) continue;
      const { validationActive } = evaluateConditional(rule, data, context);
      if (validationActive) {
        formControl.setValidators([Validators.required]);
      } else {
        formControl.clearValidators();
      }
      formControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.directorBusinessReview);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.directorBusinessReview);

    if (savedData && Object.keys(savedData).length > 0) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loading saved state:', savedData);
      
      this.businessReviewForm.patchValue(savedData, { emitEvent: false });
    }

    // Mark form as pristine after loading to prevent showing 'Edited' status on initial load
    this.businessReviewForm.markAsPristine();

    // NOTE: the isLoadingState flip + baseline snapshot
    // (markSheetInitializedWithStatus) that used to live here in a
    // setTimeout(100 → 600) chain are now owned by finalizeSheetInit(),
    // invoked via finalizeInit() after waitForMbrsHydration(). We keep ONLY the
    // synchronous prefix above (markSheetInitializing + emitEvent:false patch).
  }

  /**
   * Deterministic sheet-init finalization. Must run after EVERY loadSavedState()
   * (init AND cancel/revert) — the legacy setTimeout lived inside loadSavedState
   * and re-finalized on each call. Safe: all init-time patches use emitEvent:false.
   *
   * NB: persistInitial below re-baselines via updateSheetData(...,true) — the
   * legacy timer block did NOT do this, so this replays behaviour the timer
   * never had, it does not omit anything the timer used to do.
   *
   * Invariant: persistInitial MUST use the same accessor as this sheet's own
   * auto-save persist. This sheet (and role4/role7) currently pair `.value` in
   * both places — internally consistent and inert only because their schemas
   * (role-120000/120200/130000.form.json) declare no readonly/disabledWhen
   * controls today. The repo-wide default is `getRawValue()` (see
   * docs/troubleshooting/troubleshooting.md § "Form data" → "Form values
   * silently drop disabled controls"; Common Pitfall #2 in root CLAUDE.md) —
   * role0/role2 pair `getRawValue()` in both because their schemas DO have
   * readonly controls. If this sheet ever gains a readonly/disabledWhen
   * control, move BOTH this `persistInitial` AND its auto-save persist to
   * `getRawValue()` together — never just one, or `.value` silently stops
   * refreshing the disabled control's key and reintroduces the permanent
   * modified-vs-original divergence the pencil-reconcile hotfix removed.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.directorBusinessReview,
      form: this.businessReviewForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      // Re-baseline the transformed form value (esp. the rich-text field's
      // on-load fragment normalization) so the snapshot equals what the auto-save
      // persist writes; without it a full revert never cleared the pencil.
      persistInitial: () => {
        this.stateService.updateSheetData(
          SHEET_KEYS.directorBusinessReview,
          this.businessReviewForm.value,
          this.businessReviewForm.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
      },
    });
  }

  /**
   * Setup auto-save on form changes
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.businessReviewForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      // `isLoadingState` is the ONLY guard, matching all 25 sibling sheets.
      // It is opened by loadSavedState() and closed by finalizeSheetInit()
      // BEFORE `isLoading` goes false, so it brackets the load deterministically.
      // A second `!isUserInteraction` gate on a bare setTimeout(1500) used to sit
      // here; because buildFormEditPipeline applies shouldSkip through `filter`,
      // it DROPPED rather than deferred every edit made in the first 1.5s of an
      // already-visible form — and this pipeline is role6's only user-edit
      // persist path. See role6-user-interaction-gate-drops-edits.spec.ts.
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        const isValid = this.businessReviewForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.directorBusinessReview, this.businessReviewForm.value, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.directorBusinessReview, { hasErrors: !this.businessReviewForm.valid });
      },
    });
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    const demoData = {
      'ssmt:DisclosureOfStatementByDirectorsForBusinessReviewExplanatory':
        'BUSINESS REVIEW\n\n' +
        'Principal Activities and Business Performance:\n' +
        'The company continued its principal activities in manufacturing and trading of industrial equipment during the financial year. Revenue for the year increased by 15% compared to the previous year, driven by strong demand in the domestic market and successful expansion into new export markets.\n\n' +
        'Principal Risks and Uncertainties:\n' +
        'The company faces several key risks including foreign exchange fluctuations, supply chain disruptions, and increasing raw material costs. Management has implemented hedging strategies and diversified supplier base to mitigate these risks.\n\n' +
        'Key Performance Indicators:\n' +
        '- Revenue Growth: 15%\n' +
        '- Gross Profit Margin: 28%\n' +
        '- Return on Assets: 12%\n' +
        '- Customer Satisfaction Index: 4.2/5.0\n\n' +
        'Future Prospects:\n' +
        'The company plans to invest RM 5 million in automation and digital transformation initiatives over the next two years. We anticipate continued growth in both domestic and export markets, with a target revenue growth of 12-15% annually.',

      'ssmt:DisclosureOfBusinessReviewOnEnvironmentalEmployeesOrSocialAndCommunityIssues': 'Environmental, employee welfare and social community matters considered',
      'ssmt:EnvironmentalMatters': 'Yes',
      'ssmt:CompanysEmployees': 'Yes',
      'ssmt:SocialAndCommunityIssues': 'Yes',
      'ssmt:Others': 'No',
      'ssmt:DisclosureOfOtherBusinessReviewApplied': '',
      'ssmt:NumberOfEmployees': 150
    };

    this.businessReviewForm.patchValue(demoData);
    this.businessReviewForm.markAsDirty();

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Form has been filled with sample business review data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form data
   */
  clearForm(): void {
    this.businessReviewForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.directorBusinessReview);

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All form data has been cleared',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.businessReviewForm.invalid) {
      this.businessReviewForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    const formData = this.businessReviewForm.value;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form submitted:', formData);

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Director business review has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];

    const match = enumSource.match(/^type:(.+)$/);
    if (match && match[1]) {
      return this.dropdownOptions[match[1]] || [];
    }

    return [];
  }

  /**
   * Check if a field should be shown. Conditional fields read their schema
   * `conditional` block through the shared evaluator — the SAME source the pure
   * validator and the submit projector use, so the three cannot drift.
   */
  shouldShowField(control: FormControlSchema): boolean {
    if (control.conditional) {
      return evaluateConditional(
        control.conditional,
        this.businessReviewForm.getRawValue(),
        { filingInfo: this.stateService.getActiveFilingInfoData() }
      ).visible;
    }
    return true;
  }

  /**
   * Check if a field is required. A field carrying a `conditional` block whose
   * effect includes validation derives its required-state from the evaluator;
   * otherwise the unconditional sources (top-level `required` flag OR a
   * non-conditional `required` entry in the validators array) decide.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, {
      selfData: this.businessReviewForm?.getRawValue() ?? {},
      filingInfo: this.stateService.getActiveFilingInfoData() as any,
    });
  }

  /**
   * Check if a field has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.businessReviewForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a field (returns first error for backward compatibility)
   * Implements three-level error fallback system:
   * 1. Custom error from schema JSON (control.error.required or control.error.pattern)
   * 2. Label-based error (e.g., "Field name is required")
   * 3. Generic fallback (e.g., "This field is required")
   */
  getErrorMessage(fieldName: string): string {
    const errors = this.getAllErrorMessages(fieldName);
    return errors.length > 0 ? errors[0] : '';
  }

  /**
   * Get ALL error messages for a field (for simultaneous display)
   * Returns an array of all applicable error messages
   */
  getAllErrorMessages(fieldName: string): string[] {
    const control = this.businessReviewForm.get(fieldName);
    if (!control) {
      return [];
    }

    const errorMessages: string[] = [];
    const schemaControl = this.schema?.controls.find(control => control.qname === fieldName);

    // Check if field has been touched or dirty
    if (!control.touched && !control.dirty) {
      return [];
    }

    // Handle required error
    if (control.errors?.['required']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.required) {
        errorMessages.push(schemaControl.error.required);
      } else if (schemaControl?.label) {
        // Level 2: Label-based error
        errorMessages.push(`${schemaControl.label} is required`);
      } else {
        // Level 3: Generic fallback
        errorMessages.push('This field is required');
      }
    }

    // Handle min error
    if (control.errors?.['min']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.['min']) {
        errorMessages.push(schemaControl.error['min']);
      } else {
        // Check if there's a validator with a custom message
        const validator = schemaControl?.validators?.find(validator => validator.name === 'min');
        if (validator && (validator as any).message) {
          errorMessages.push((validator as any).message);
        } else if (schemaControl?.label) {
          // Level 2: Label-based error
          errorMessages.push(`${schemaControl.label} must be a non-negative number`);
        } else {
          // Level 3: Generic fallback
          errorMessages.push('Must be a non-negative number');
        }
      }
    }

    // Handle pattern error
    if (control.errors?.['pattern']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.pattern) {
        errorMessages.push(schemaControl.error.pattern);
      } else {
        // Check if there's a validator with a custom message
        const validator = schemaControl?.validators?.find(validator => validator.name === 'pattern');
        if (validator && (validator as any).message) {
          errorMessages.push((validator as any).message);
        } else if (schemaControl?.label) {
          // Level 2: Label-based error
          errorMessages.push(`${schemaControl.label} has invalid format`);
        } else {
          // Level 3: Generic fallback
          errorMessages.push('Invalid format');
        }
      }
    }

    // Handle other error types (extensible)
    if (control.errors) {
      Object.keys(control.errors).forEach(errorKey => {
        if (errorKey !== 'required' && errorKey !== 'min' && errorKey !== 'pattern' && schemaControl?.error?.[errorKey]) {
          const errorMsg = schemaControl.error[errorKey];
          if (!errorMessages.includes(errorMsg)) {
            errorMessages.push(errorMsg);
          }
        }
      });
    }

    return errorMessages;
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.businessReviewForm.get(qname);
  }

  /**
   * Get field warnings (for display in template)
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Split warning message to highlight asterisk (*)
   * Example: "Field must be (*)" -> [{ text: "Field must be (", isAsterisk: false }, { text: "*", isAsterisk: true }, { text: ")", isAsterisk: false }]
   */
  splitWarningMessage(message: string): { text: string; isAsterisk: boolean }[] {
    const parts: { text: string; isAsterisk: boolean }[] = [];
    let lastIndex = 0;

    // Find all asterisks in the message
    for (let charIndex = 0; charIndex < message.length; charIndex++) {
      if (message[charIndex] === '*') {
        // Add text before asterisk
        if (charIndex > lastIndex) {
          parts.push({
            text: message.substring(lastIndex, charIndex),
            isAsterisk: false
          });
        }
        // Add asterisk
        parts.push({
          text: '*',
          isAsterisk: true
        });
        lastIndex = charIndex + 1;
      }
    }

    // Add remaining text after last asterisk
    if (lastIndex < message.length) {
      parts.push({
        text: message.substring(lastIndex),
        isAsterisk: false
      });
    }

    return parts;
  }

  /**
   * Setup warning evaluation system
   * Watches form changes and evaluates warnings in real-time
   */
  private setupWarningEvaluation(): void {
    // Watch form value changes
    this.businessReviewForm.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // directorBusinessReview slice, `applyRemoteChange` rehydrates the
    // shared state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges), which is what makes the local
    // valueChanges-driven re-evaluation above moot here — the field-level
    // warning indicators are refreshed as part of adoption instead.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.directorBusinessReview)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.directorBusinessReview)),
        filter(() => !!this.schema && !!this.businessReviewForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Pure-refactor extraction of the previous inline warning-evaluation
   * subscription body. Re-used by both the local valueChanges subscription
   * and the cross-tab remoteUpdateApplied$ subscription so the field-level
   * warning indicators stay accurate on both code paths.
   */
  private evaluateFieldWarnings(): void {
    // Build warnings map from schema
    const warningRulesByField: { [qname: string]: any[] } = {};
    this.schema.controls.forEach(control => {
      if (control.warnings && control.warnings.length > 0) {
        warningRulesByField[control.qname] = control.warnings;
      }
    });

    // Merge cross-sheet data for validation
    const allFormData = this.mergeFormDataForValidation();

    // Evaluate warnings for each field
    Object.keys(warningRulesByField).forEach(qname => {
      const control = this.businessReviewForm.get(qname);
      const rules = warningRulesByField[qname];

      if (control) {
        const warnings = this.warningService.evaluateWarnings(
          control as FormControl,
          rules,
          allFormData,
          true // isEditedMode - always true for now
        );

        if (warnings.length > 0) {
          this.fieldWarnings[qname] = warnings;
        } else {
          delete this.fieldWarnings[qname];
        }
      }
    });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.businessReviewForm, this.stateService.getSheetData(SHEET_KEYS.directorBusinessReview));
    this.evaluateFieldWarnings();
  }

  /**
   * Merge form data from multiple sheets for cross-sheet validation
   */
  private mergeFormDataForValidation(): any {
    const currentFormData = this.businessReviewForm.getRawValue();

    // Get data from other sheets if needed for cross-sheet validation
    const filingPrerequisites = this.stateService.getActiveFilingInfoData() || {};

    return {
      ...currentFormData,
      ...filingPrerequisites
    };
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden based on schema's hideWhen configuration
   *
   * For inherited fields: Also checks if field exists in Role-1 (filingInfo)
   */
  shouldHideField(fieldQname: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control) {
      return false;
    }

    // Read from the active filing-info source for the current phase
    // (filingPrerequisites in Phase 1, filingInfo in Phase 2). See PR-F.
    const filingInfoData = this.stateService.getActiveFilingInfoData();

    // For inherited fields: Hide if field doesn't exist or is empty upstream
    if (control.ui.inheritFrom === 'filingInfo') {
      const role1Value = filingInfoData?.[fieldQname];

      // Hide if field is not present or is empty/null/undefined in Role-1
      if (
        role1Value === undefined ||
        role1Value === null ||
        role1Value === ''
      ) {
        return true;
      }
    }

    // Schema-driven hideWhen logic
    if (control.ui.hideWhen) {
      const { field, values } = control.ui.hideWhen;
      const triggerValue = filingInfoData?.[field];

      if (values.includes(triggerValue)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a field has a meaningful value (not null, undefined, or empty string)
   */
  private fieldHasValue(control: FormControlSchema, allFormData: any): boolean {
    // Handle dimensional fields
    if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
      return control.ContextPolicy.allowedAxes.some(axis => {
        const fieldName = `${control.qname}[${axis}]`;
        const value = allFormData[fieldName];
        return value !== null && value !== undefined && value !== '';
      });
    }

    // Handle regular fields
    const value = allFormData[control.qname];
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Get all visible fields for submission
   * Returns all visible fields based on schema visibility rules
   * Filters out unused fields (CLBG, scope-specific, etc.) based on submission type
   * CRITICAL: Transforms frontend bracket notation to backend nested format
   */
  getVisibleFormData(): any {
    const allFormData = this.businessReviewForm.getRawValue();
    const submissionData: any = {};

    // Dynamically collect all visible fields based on schema
    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip fields that should be hidden (based on hideWhen rules or inherited fields not in Role-0)
      if (this.shouldHideField(control.qname)) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping hidden field: ${control.qname}`);
        return;
      }

      // Check conditional visibility (requiredIf)
      if (control.requiredIf) {
        const dependentControl = this.businessReviewForm.get(control.requiredIf.qname);
        if (dependentControl?.value !== control.requiredIf.equals) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Check conditional visibility (requiredIfAny)
      if (control.requiredIfAny) {
        const shouldShow = control.requiredIfAny.some((condition) => {
          const dependentControl = this.businessReviewForm.get(condition.qname);
          return dependentControl?.value === condition.equals;
        });
        if (!shouldShow) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Skip fields with empty values (optional optimization)
      // This reduces payload size by not sending empty optional fields
      const hasValue = this.fieldHasValue(control, allFormData);
      if (!control.required && !hasValue) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping empty optional field: ${control.qname}`);
        return;
      }

      // Handle fields with dimensional axes (e.g., MSICCode with business members)
      if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
        control.ContextPolicy.allowedAxes.forEach(axis => {
          const fieldName = `${control.qname}[${axis}]`;
          if (allFormData.hasOwnProperty(fieldName)) {
            submissionData[fieldName] = allFormData[fieldName];
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`✅ Including dimensional field: ${fieldName} = ${allFormData[fieldName]}`);
          }
        });
      } else {
        // Regular field without axes
        const fieldName = control.qname;
        if (allFormData.hasOwnProperty(fieldName)) {
          submissionData[fieldName] = allFormData[fieldName];
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Including field: ${fieldName}`);
        }
      }
    });

    return submissionData;
  }

  /**
   * Get max length for a field
   * Returns 12 if type is MyKad/MyPR/MyKAS identification number
   * Returns 255 for all other text controls
   * Returns null for non-text controls
   */
  getMaxLength(fieldName: string): number | null {
    return getFieldMaxLength(fieldName, this.schema);
  }

  /**
   * Determine if an identification number field should be numeric-only
   * Returns true if the corresponding type field is MyKad/MyPR/MyKAS
   */
  shouldBeNumericOnly(fieldName: string): boolean {
    const typeValue = this.businessReviewForm.get(identificationTypeFieldFor(fieldName))?.value;
    return isNumericOnlyIdentificationField(fieldName, typeValue);
  }

  /** Memoised per-control policy — templates call this once per binding. */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FormControlSchema): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.businessReviewForm.markAsPristine();
    // Re-finalize after the revert: the legacy setTimeout inside loadSavedState
    // used to do this on every call; now finalizeInit() owns it.
    void this.finalizeInit();
  }
}
